import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  // Check for existing token on app load
  useEffect(() => {
    // Login flow disabled - automatically authenticate
    const mockUser: User = {
      id: '1',
      email: 'demo@example.com',
      name: 'Demo User',
      roles: ['USER']
    };
    
    setUser(mockUser);
    setIsAuthenticated(true);
    setIsLoading(false);
    
    // Original authentication logic commented out
    // const storedToken = localStorage.getItem('token');
    // const storedUser = localStorage.getItem('user');
    // 
    // if (storedToken && storedUser) {
    //   setToken(storedToken);
    //   setUser(JSON.parse(storedUser));
    //   setIsAuthenticated(true);
    // }
    // 
    // setIsLoading(false);
  }, []);

  // Login function disabled - commenting out authentication logic
  const login = async (email: string, password: string) => {
    // Authentication disabled - mock successful login
    const mockUser: User = {
      id: '1',
      email: email,
      name: email.split('@')[0],
      roles: ['USER']
    };
    
    setUser(mockUser);
    setIsAuthenticated(true);
    
    // Original login logic commented out
    // try {
    //   setIsLoading(true);
    //   
    //   const response = await authAPI.login(email, password);
    //   const { accessToken, refreshToken, expirationTimeInUTC, roles } = response.data;
    //   
    //   // Store tokens in localStorage
    //   localStorage.setItem('token', accessToken);
    //   localStorage.setItem('refreshToken', refreshToken);
    //   localStorage.setItem('expirationTimeInUTC', expirationTimeInUTC);
    //   
    //   // Create user object
    //   const userData: User = {
    //     id: '1', // Replace with actual user ID from response
    //     email,
    //     name: email.split('@')[0], // Simple name extraction
    //     roles: roles || ['USER']
    //   };
    //   
    //   localStorage.setItem('user', JSON.stringify(userData));
    //   
    //   setToken(accessToken);
    //   setUser(userData);
    //   setIsAuthenticated(true);
    //   
    // } catch (error) {
    //   console.error('Login error:', error);
    //   throw new Error('Login failed. Please check your credentials.');
    // } finally {
    //   setIsLoading(false);
    // }
  };

  // Logout function disabled - commenting out authentication logic
  const logout = () => {
    // Authentication disabled - mock logout (does nothing)
    console.log('Logout disabled - authentication bypassed');
    
    // Original logout logic commented out
    // // Clear localStorage
    // localStorage.removeItem('token');
    // localStorage.removeItem('refreshToken');
    // localStorage.removeItem('expirationTimeInUTC');
    // localStorage.removeItem('user');
    // 
    // // Clear state
    // setToken(null);
    // setUser(null);
    // setIsAuthenticated(false);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 